import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/esm/Add';
import DeleteIcon from '@mui/icons-material/esm/Delete';
import CheckIcon from '@mui/icons-material/esm/Check';
import CloseIcon from '@mui/icons-material/esm/Close';
import EditIcon from '@mui/icons-material/esm/Edit';
import resourcesService, { ResourceCatalogItem } from '../../services/api/resourcesService';
import taskMaterialsService, { TaskMaterial } from '../../services/api/taskMaterialsService';
import { yardZoneGuidanceForResource, yardZoneLabel } from '../../utils/yardZoneGuidance';

/**
 * Materials section for the Task Detail page. Lists task_resources rows with
 * resource info inlined; supports add (picker), per-row quantity adjustment,
 * and per-row delete.
 *
 * Allocation / consumption columns are read-only here — those move through
 * the inventory ledger via PR #37's procurement work, not this surface.
 */
interface MaterialsBoxProps {
  taskId: string;
  showYardZone?: boolean;
}

export function MaterialsBox({ taskId, showYardZone = true }: MaterialsBoxProps) {
  const [materials, setMaterials] = useState<TaskMaterial[]>([]);
  const [catalog, setCatalog] = useState<ResourceCatalogItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Add-row state
  const [adding, setAdding] = useState(false);
  const [pickedResource, setPickedResource] = useState<ResourceCatalogItem | null>(null);
  const [pickedQty, setPickedQty] = useState<string>('1');

  // Per-row edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingQty, setEditingQty] = useState<string>('');

  const load = async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const [mats, cat] = await Promise.all([
        taskMaterialsService.listForTask(taskId),
        catalog.length === 0 ? resourcesService.list() : Promise.resolve(catalog),
      ]);
      setMaterials(mats);
      if (cat) setCatalog(cat);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load materials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  // Hide resources already on the task from the picker — server enforces
  // uniqueness anyway but this keeps the UX clean.
  const pickerOptions = useMemo(() => {
    const taken = new Set(materials.map((m) => m.resourceId));
    return catalog.filter((r) => !taken.has(r.id));
  }, [catalog, materials]);

  const startAdd = () => {
    setAdding(true);
    setPickedResource(null);
    setPickedQty('1');
    setError(null);
  };

  const cancelAdd = () => {
    setAdding(false);
    setPickedResource(null);
    setPickedQty('1');
  };

  const submitAdd = async () => {
    if (!pickedResource) {
      setError('Pick a resource first');
      return;
    }
    const qty = Number(pickedQty);
    if (!Number.isFinite(qty) || qty <= 0) {
      setError('Quantity must be a positive number');
      return;
    }
    try {
      await taskMaterialsService.addToTask(taskId, {
        resourceId: pickedResource.id,
        quantityRequired: qty,
      });
      cancelAdd();
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to add material');
    }
  };

  const startEdit = (m: TaskMaterial) => {
    setEditingId(m.id);
    setEditingQty(String(m.quantityRequired));
    setError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingQty('');
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const qty = Number(editingQty);
    if (!Number.isFinite(qty) || qty <= 0) {
      setError('Quantity must be a positive number');
      return;
    }
    try {
      await taskMaterialsService.adjustQuantity(taskId, editingId, qty);
      cancelEdit();
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to adjust quantity');
    }
  };

  const handleDelete = async (m: TaskMaterial) => {
    if (!window.confirm(`Remove "${m.resource?.name ?? m.resourceId}" from this task?`)) return;
    try {
      await taskMaterialsService.removeFromTask(taskId, m.id);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to remove material');
    }
  };

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          Materials
        </Typography>
        {!adding && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={startAdd}
            disabled={loading}
          >
            Add material
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {adding && (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
          <Autocomplete
            size="small"
            options={pickerOptions}
            value={pickedResource}
            onChange={(_, v) => setPickedResource(v)}
            getOptionLabel={(opt) => `${opt.name} (${opt.unitOfMeasure})`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            sx={{ flex: 1, minWidth: 240 }}
            renderInput={(params) => (
              <TextField {...params} label="Resource" placeholder="Search by name or code" />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Stack>
                  <Typography variant="body2">{option.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.resourceCode} · {option.unitOfMeasure} · {option.resourceType}
                  </Typography>
                </Stack>
              </li>
            )}
          />
          <TextField
            size="small"
            type="number"
            label="Qty"
            value={pickedQty}
            onChange={(e) => setPickedQty(e.target.value)}
            inputProps={{ min: 0, step: 'any' }}
            sx={{ width: 100 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submitAdd();
              }
            }}
          />
          <Button variant="contained" size="small" onClick={submitAdd}>Add</Button>
          <Button size="small" onClick={cancelAdd}>Cancel</Button>
        </Stack>
      )}

      {materials.length === 0 && !loading ? (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
          No materials assigned yet.
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
                  <TableCell>Material</TableCell>
                  {showYardZone && <TableCell sx={{ minWidth: 170 }}>Yard zone</TableCell>}
                  <TableCell align="right" sx={{ width: 110 }}>Required</TableCell>
                  <TableCell align="right" sx={{ width: 110 }}>Allocated</TableCell>
                  <TableCell align="right" sx={{ width: 110 }}>Consumed</TableCell>
              <TableCell align="right" sx={{ width: 110 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {materials.map((m) => {
              const isEditing = editingId === m.id;
              const uom = m.resource?.unitOfMeasure ?? '';
              const yardZone = yardZoneGuidanceForResource({
                name: m.resource?.name,
                resourceCode: m.resource?.resourceCode,
                resourceType: m.resource?.resourceType,
                category: m.resource?.category,
              });
              return (
                <TableRow key={m.id} hover>
                  <TableCell>
                    <Typography variant="body2">{m.resource?.name ?? m.resourceId}</Typography>
                    {m.resource?.resourceCode && (
                      <Typography variant="caption" color="text.secondary">
                        {m.resource.resourceCode}
                      </Typography>
                    )}
                  </TableCell>
                  {showYardZone && (
                    <TableCell>
                      <Typography variant="body2" fontWeight={800}>
                        {yardZoneLabel(yardZone)}
                      </Typography>
                      {yardZone && (
                        <Typography variant="caption" color="text.secondary">
                          Based on {yardZone.reason}
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  <TableCell align="right">
                    {isEditing ? (
                      <TextField
                        size="small"
                        type="number"
                        autoFocus
                        value={editingQty}
                        onChange={(e) => setEditingQty(e.target.value)}
                        inputProps={{ min: 0, step: 'any', style: { textAlign: 'right' } }}
                        sx={{ width: 90 }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            saveEdit();
                          }
                          if (e.key === 'Escape') cancelEdit();
                        }}
                      />
                    ) : (
                      <Typography variant="body2">
                        {Number(m.quantityRequired)} {uom}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {Number(m.quantityAllocated ?? 0)} {uom}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {Number(m.quantityConsumed ?? 0)} {uom}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {isEditing ? (
                      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                        <Tooltip title="Save">
                          <IconButton size="small" onClick={saveEdit}>
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton size="small" onClick={cancelEdit}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                        <Tooltip title="Adjust quantity">
                          <IconButton size="small" onClick={() => startEdit(m)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton size="small" color="error" onClick={() => handleDelete(m)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
